import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { type ReactNode, useState } from 'react';

import { MOTION_ALLOWED } from '@/motion/queries';

type TextTag = 'h1' | 'h2' | 'h3' | 'p';

interface RevealTextProps {
  readonly as?: TextTag;
  readonly id?: string;
  readonly className?: string;
  readonly delay?: number;
  /** `scroll` : à l'entrée dans l'écran ; `mount` : dès l'affichage. */
  readonly trigger?: 'scroll' | 'mount';
  readonly children: ReactNode;
}

/**
 * Titre révélé ligne par ligne, chaque ligne glissant hors d'un masque.
 * Le découpage attend le chargement des polices, puis est défait à la fin
 * de l'animation : le texte redevient un texte normal, qui se recompose librement.
 */
export function RevealText({
  as: Tag = 'h2',
  id,
  className,
  delay = 0,
  trigger = 'scroll',
  children,
}: RevealTextProps) {
  const [element, setElement] = useState<HTMLElement | null>(null);

  useGSAP(
    (_context, contextSafe) => {
      if (!element || !contextSafe || !window.matchMedia(MOTION_ALLOWED).matches) return;

      const state: { split: SplitText | null } = { split: null };
      gsap.set(element, { autoAlpha: 0 });

      const reveal = contextSafe(() => {
        const split = SplitText.create(element, { type: 'lines', mask: 'lines' });
        state.split = split;
        gsap.set(element, { autoAlpha: 1 });
        gsap.from(split.lines, {
          yPercent: 112,
          duration: 1.2,
          ease: 'expo.out',
          stagger: 0.09,
          delay,
          onComplete: () => {
            split.revert();
          },
          scrollTrigger:
            trigger === 'scroll' ? { trigger: element, start: 'top 88%', once: true } : undefined,
        });
      });
      void document.fonts.ready.then(() => {
        reveal();
      });

      return () => {
        state.split?.revert();
      };
    },
    { dependencies: [element, delay, trigger] },
  );

  return (
    <Tag ref={setElement} id={id} className={className}>
      {children}
    </Tag>
  );
}
